import { DateTimeDisplay } from "@kalyzee/escaet-ui";
import { useMeetingSessionOvenMediaReceiverManager } from "@kalyzee/kast-web-sdk";
import { ChatRef, useRefAutoUpdate } from "@kalyzee/kast-web-sdk/web-components";
import {
  MeetingRoomStatus,
  MeetingRoomType,
  MeetingSession,
  MeetingSessionHighlightingState,
  MeetingSessionMedia,
  MeetingState,
} from "@kalyzee/kast-web-sdk/websocket";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import SamiiEscaetLogo from "../../../assets/logo/samii-escaet.svg";
import { ScreenMediaName } from "../../../components/media/ScreenShare";
import { PageSlaveMode } from "../../../components/navigation/PageContext";
import PauseView from "../../../components/utils/PauseView";
import { CarouselData } from "../../../helpers/bridge/common";
import { useMeetingSlaveMessagingBridge } from "../../../hooks/meetingMessagingBridge";
import { useScreenParams } from "../../../hooks/screen";
import styles from "./meeting.module.css";
import { SecondaryScreen1 } from "./screens/screen1";
import { SecondaryScreen2 } from "./screens/screen2";
import { SecondaryScreenScreenSharing } from "./screens/screenSharing";
import { SecondaryScreenX } from "./screens/screenX";

export interface MeetingSlavePageProps {}

export const DEFAULT_PANEL_WIDTH = "25%";
export const DEFAULT_GROUP_PANEL_WIDTH = "33%";
export const SCREEN_INDEX_SCREENSHARING = -1;

export type SlaveCarouselData = {
  data: CarouselData;
  page: number;
  pageCount: number;
};

const MeetingSlavePage = ({}: MeetingSlavePageProps) => {
  const chatRef = useRef<ChatRef>(null);
  const bridgeData = useMeetingSlaveMessagingBridge(chatRef);
  const bridgeDataRef = useRefAutoUpdate(bridgeData);
  const screenParams = useScreenParams();
  const { bridge, meeting, rooms, session, qrCodeData, currenExpertView } = bridgeData;

  const [data, setData] = useState<SlaveCarouselData>({ page: 0, pageCount: 0, data: { mode: "sessions", sessions: [] } });
  const [sessionsToPreload, setSessionsToPreload] = useState<MeetingSession[]>([]);

  const highlightRequests = useMemo(
    () => meeting?.sessions?.filter((s) => s.highlighting === MeetingSessionHighlightingState.PENDING_REQUEST) ?? [],
    [meeting]
  );

  const groupSlots = screenParams.index === 1 || screenParams.index === 2 ? 2 : 3;

  const { manager: receptionManager, sessions: videoSessions } = useMeetingSessionOvenMediaReceiverManager(
    meeting,
    useCallback(
      (session: MeetingSession, media: MeetingSessionMedia) => {
        if (screenParams.index === SCREEN_INDEX_SCREENSHARING) {
          const { rooms, session: ownSession } = bridgeDataRef.current;

          if (
            currenExpertView?.type === "activity_all_leaders" ||
            currenExpertView?.type === "activity_one_group" ||
            currenExpertView?.type === "activity_one_group_spy" ||
            currenExpertView?.type === "activity_one_leader"
          ) {
            const currentOngoingRoom = rooms?.find((r) => r.id === currenExpertView?.groupId);
            if (currentOngoingRoom?.enlistedUserIds?.find((id) => session.user?.id === id)) {
              return media.name === ScreenMediaName;
            }
          }

          const currentHighlightingRoom = rooms?.find(
            (r) => r.type === MeetingRoomType.Activity && r.status === MeetingRoomStatus.Enlistment && r.enlistedUserIds?.find((id) => session.user?.id === id)
          );

          // if user is in ongoing group
          if (currentHighlightingRoom) {
            // show only if group is highlighting
            return media.name === ScreenMediaName;
          }
          return session.id === ownSession?.id && media.name === ScreenMediaName;
        }
        if (media.name === ScreenMediaName) return false;
        if (screenParams.index === 2 && highlightRequests.find((s) => s.id === session.id)) return true;
        if (sessionsToPreload.find((s) => s.id === session.id)) return true;
        if (data.data.mode === "sessions") return data.data.sessions.some((s) => s.id === session.id);
        return data.data.groups.some((g) => g.sessions.some((s) => s.id === session.id));
      },
      [data, sessionsToPreload, currenExpertView, highlightRequests, screenParams.index]
    )
  );

  const timeoutDisplaySessionsRef = useRef<NodeJS.Timeout>();

  // ---------------------------------------------------------------------- //
  // ---------------------------   UTILS ----------------------------- //
  // ---------------------------------------------------------------------- //

  const clean = () => {
    bridgeDataRef.current?.bridge?.postClearClientData(PageSlaveMode.MEETING);
    clearTimeout(timeoutDisplaySessionsRef.current);
  };

  // ---------------------------------------------------------------------- //
  // --------------------------- EFFECTS ----------------------------- //
  // ---------------------------------------------------------------------- //

  useEffect(() => {
    bridgeDataRef.current?.bridge?.postClientData(PageSlaveMode.MEETING, {
      screen: screenParams.index,
      players: screenParams.rows * screenParams.columns,
      groups: groupSlots,
    });
  }, [screenParams, meeting]);

  useEffect(() => {
    if (meeting?.state === MeetingState.PAUSED) {
      receptionManager.pause();
    } else {
      receptionManager.restore();
    }
  }, [meeting, receptionManager]);

  useEffect(() => {
    if (!bridge) return;
    const displayCarouselListener = bridge.addEventListener("displayCarousel", (msg) => {
      const { data, delay, page, pageCount } = msg.content;
      if (delay <= 0) {
        clearTimeout(timeoutDisplaySessionsRef.current);
        setSessionsToPreload([]);
        setData({ page, pageCount, data });
      } else {
        setSessionsToPreload(data.mode === "sessions" ? data.sessions : data.groups.flatMap((g) => g.visibleSessions));
        timeoutDisplaySessionsRef.current = setTimeout(() => {
          setSessionsToPreload([]);
          setData({ page, pageCount, data });
        }, delay);
      }
    });
    return () => {
      displayCarouselListener.flush();
    };
  }, [bridge]);

  useEffect(() => {
    if (!meeting || !session) clean();
  }, [meeting, session]);

  useEffect(() => {
    return () => {
      clean();
    };
  }, []);

  // ---------------------------------------------------------------------- //
  // ---------------------------   RENDER --------------------------------- //
  // ---------------------------------------------------------------------- //

  if (!meeting || !session) {
    const screenName = screenParams?.name ?? (screenParams.index !== undefined ? `Écran n°${screenParams.index}` : undefined);
    return (
      <div className={[styles.waiting, styles.background].join(" ")}>
        <div className={styles.header}>
          <img src={SamiiEscaetLogo} alt="logo" />
        </div>
        <div className={styles.content}>{"En attente d'un événement"}</div>
        <div className={styles.footer}>
          <DateTimeDisplay cx="textLeft" />
          {screenName ? <div style={{ color: "rgb(136, 136, 136)", fontFamily: "Nunito, sans-serif", fontWeight: "bold" }}>{screenName}</div> : null}
        </div>
      </div>
    );
  }

  if (screenParams.index <= 0 && screenParams.index !== SCREEN_INDEX_SCREENSHARING) {
    const screenName = screenParams?.name ?? (screenParams.index !== undefined ? `Écran n°${screenParams.index}` : undefined);
    return (
      <div className={[styles.waiting, styles.background].join(" ")}>
        <div className={styles.header}>
          <img src={SamiiEscaetLogo} alt="logo" />
        </div>
        <div className={styles.content}>{"Écran non configuré"}</div>
        <div className={styles.footer}>
          <DateTimeDisplay cx="textLeft" />
          {screenName ? <div style={{ color: "rgb(136, 136, 136)", fontFamily: "Nunito, sans-serif", fontWeight: "bold" }}>{screenName}</div> : null}
        </div>
      </div>
    );
  }

  if (screenParams.index === SCREEN_INDEX_SCREENSHARING) {
    return (
      <SecondaryScreenScreenSharing
        meeting={meeting}
        session={session}
        videoSessions={videoSessions}
        screenParams={screenParams}
        className={styles.container}
      ></SecondaryScreenScreenSharing>
    );
  }

  if (screenParams.index === 1) {
    return (
      <SecondaryScreen1
        meeting={meeting}
        videoSessions={videoSessions}
        screenParams={screenParams}
        carouselData={data}
        qrCodeData={qrCodeData}
        chatRef={chatRef}
        className={styles.container}
      >
        {meeting?.state === MeetingState.PAUSED ? <PauseView meeting={meeting} /> : null}
      </SecondaryScreen1>
    );
  }

  if (screenParams.index === 2) {
    return (
      <SecondaryScreen2
        meeting={meeting}
        videoSessions={videoSessions}
        screenParams={screenParams}
        carouselData={data}
        rooms={rooms}
        className={styles.container}
      >
        {meeting?.state === MeetingState.PAUSED ? <PauseView meeting={meeting} /> : null}
      </SecondaryScreen2>
    );
  }

  return (
    <SecondaryScreenX meeting={meeting} videoSessions={videoSessions} screenParams={screenParams} carouselData={data} className={styles.container}>
      {meeting?.state === MeetingState.PAUSED ? <PauseView meeting={meeting} /> : null}
    </SecondaryScreenX>
  );
};

export default MeetingSlavePage;
